import {
  getLanguageDisplayName,
  getOrgLanguages,
  translateBackend,
} from '@assets/i18n/config';
import { CountryName } from '@components/fields/countryName';
import type { CustomField } from '@interfaces/customField';
import type { EnabledJurisdictions } from '@interfaces/organization';
import { EntityTypes } from '@interfaces/user';
import { Box } from '@ovComponents/1-primative/box/box';
import { Grid } from '@ovComponents/1-primative/grid/grid';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { AddressField } from '@ovComponents/2-component/addressField/addressField';
import { Alert } from '@ovComponents/2-component/alert/alert';
import AlternativeAddressField from '@ovComponents/2-component/alternativeAddressField/alternativeAddressField';
import { DateField } from '@ovComponents/2-component/dateField/dateField';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { useCustomFieldRenderer } from '@ovComponents/3-pattern/customField/useCustomFieldRenderer';
import { PhoneNumberField } from '@ovComponents/3-pattern/phoneNumberField/phoneNumberField';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { UserContext } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { phoneValidator } from '@utils/common';
import dayjs from 'dayjs';
import type { CountryCode } from 'libphonenumber-js';
import { kebabCase } from 'lodash-es';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { buildEnrichedCustomOptions, invalidFields } from '../utils';

export const NonIndividualInformationVisual = ({
  options,
  userData,
  loading,
  updateUser,
  continueFunc,
  grid = false,
  updateMode = false,
  activeCustomFields,
  derivedCustomFields,
  workflowCompletion,
  ignoreCustomFieldTriggerRules,
  applicableJurisdictions = { all: true },
  hasMailingAddress,
  hasAlternateAddress,
  setHasMailingAddress,
  setHasAlternateAddress,
}: {
  options: any;
  userData: any;
  loading: boolean;
  grid?: boolean;
  updateMode?: boolean;
  activeCustomFields?: string[];
  derivedCustomFields?: CustomField[];
  workflowCompletion?: any;
  updateUser: (user: any) => void;
  continueFunc: () => void;
  ignoreCustomFieldTriggerRules?: boolean;
  applicableJurisdictions?: EnabledJurisdictions;
  hasMailingAddress: boolean;
  hasAlternateAddress: boolean;
  setHasMailingAddress: (hasMailingAddress: boolean) => void;
  setHasAlternateAddress: (hasAlternateAddress: boolean) => void;
}) => {
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { activeOrganization } = useContext(UserContext);
  const { t } = useTranslation(['client', 'entityTypes', 'corporations']);
  const [focused, setFocused] = useState<string[]>([]);
  const [invalidFieldsList, setInvalidFieldsList] = useState<string[]>([]);
  const [updated, setUpdated] = useState<boolean>(false);
  const [customOptionSelected, setCustomOptionSelected] = useState<any[]>([]);
  const { renderCustomFields, validateAllCustomFields } =
    useCustomFieldRenderer({
      customOptions: customOptionSelected,
      customFieldValues: userData.customFields,
    });
  const [invalidPhone, setInvalidPhone] = useState<boolean>(false);
  const [phoneCountryCode, setPhoneCountryCode] = useState<
    CountryCode | undefined
  >(undefined);

  useEffect(() => {
    setCustomOptionSelected(
      buildEnrichedCustomOptions(
        options,
        activeCustomFields,
        derivedCustomFields,
      ),
    );
  }, [options, activeCustomFields, derivedCustomFields]);

  const submit = () => {
    const fields = invalidFields(
      {
        ...options,
        inProvinceSince: {
          ...options.inProvinceSince,
          workflowId: workflowCompletion?.workflow.id,
          requiredIf: (data: any) => data?.physicalAddress?.province === 'QC',
        },
        mailingAddress: {
          ...options.mailingAddress,
          requiredIf: (data: any) => data?.hasMailingAddress,
        },
        alternateAddress: {
          ...options.alternateAddress,
          requiredIf: (data: any) => data?.hasAlternateAddress,
        },
        typeOfTrust: {
          ...options.typeOfTrust,
          requiredIf: (data: any) => data?.type === EntityTypes.TRUST,
        },
      },
      {
        ...userData,
        hasMailingAddress,
        hasAlternateAddress,
      },
    );

    if (physicalAddressInvalid) fields.push('physicalAddress');
    if (mailingAddressInvalid) fields.push('mailingAddress');
    if (alternateAddressInvalid) fields.push('alternateAddress');

    if (userData?.phone && !phoneValidator(userData?.phone, phoneCountryCode)) {
      fields.push('phone');
      setInvalidPhone(true);
    } else {
      setInvalidPhone(false);
    }

    const invalidCustomFieldKeys = validateAllCustomFields();
    fields.push(...invalidCustomFieldKeys);
    setFocused(fields);
    setInvalidFieldsList(fields);

    if (fields.length === 0) {
      sendAnalytic(
        ovAnalyticsEvents.workflowsNonIndividualInformationContinueButtonSelect,
        {
          workflowStepTitle: options?.title,
          workflowId: workflowCompletion?.workflow?.id,
          workflowName: workflowCompletion?.workflow?.name,
          activeWorkflowCompletionId: workflowCompletion?.id,
          objectId: workflowCompletion?.objectId,
          objectType: workflowCompletion?.objectType,
        },
      );
      continueFunc();
      setUpdated(false);
    }
  };

  const update = (newValue: any) => {
    setUpdated(true);
    updateUser(newValue);
  };

  const lockMessage = () =>
    options.customLockedMessage
      ? translateBackend(options.customLockedMessage)
      : t('pageConfiguration:notEditableMessage');

  const error = (key: string): boolean =>
    ((!userData?.[key] && focused.includes(key)) ||
      invalidFieldsList.includes(key)) &&
    options?.[key]?.required !== FieldRequiredType.NOT_REQUIRED;

  const getAddressOutOfJurisdiction = ({
    addressOptions,
    applicableJurisdictionsToCheck,
    addressUserData,
  }: {
    addressOptions: any;
    applicableJurisdictionsToCheck?: EnabledJurisdictions;
    addressUserData: any;
  }) => {
    if (!addressOptions?.enabled && !addressOptions?.jurisdiction?.enabled)
      return false;
    if (!addressUserData) return false;
    if (!addressUserData?.jurisdiction) return false;

    if (!applicableJurisdictionsToCheck) return false;

    if (applicableJurisdictionsToCheck.all === true) return false;
    if (
      !applicableJurisdictionsToCheck.only ||
      applicableJurisdictionsToCheck.only.length === 0
    )
      return false;

    if (
      applicableJurisdictionsToCheck.only.includes(
        addressUserData?.jurisdiction,
      )
    )
      return false;

    const errMessage =
      translateBackend(addressOptions?.addressOutOfJurisdictionMessage) ||
      t('client:profile.fieldValidations.addressOutOfJurisdictionMessage');
    return errMessage.replace(
      '{{ALLOWED_JURISDICTIONS}}',
      (applicableJurisdictionsToCheck?.only ?? [])
        ?.map((j: any) => j.replace(/^(CA|US)_/, ''))
        .join(', '),
    );
  };

  const physicalAddressOutOfJurisdiction: false | string = useMemo(
    () =>
      getAddressOutOfJurisdiction({
        addressOptions: options.physicalAddress,
        applicableJurisdictionsToCheck: applicableJurisdictions,
        addressUserData: userData.physicalAddress,
      }),
    [options.physicalAddress, userData, applicableJurisdictions],
  );

  const mailingAddressOutOfJurisdiction: false | string = useMemo(
    () =>
      getAddressOutOfJurisdiction({
        addressOptions: options.mailingAddress,
        applicableJurisdictionsToCheck: applicableJurisdictions,
        addressUserData: userData.mailingAddress,
      }),
    [options.mailingAddress, userData, applicableJurisdictions],
  );

  const physicalAddressInvalid =
    physicalAddressOutOfJurisdiction !== false &&
    options?.physicalAddress?.required === FieldRequiredType.ERROR;
  const mailingAddressInvalid =
    mailingAddressOutOfJurisdiction !== false &&
    options?.mailingAddress?.required === FieldRequiredType.ERROR &&
    hasMailingAddress;
  const alternateAddressInvalid =
    (!userData?.alternateAddress?.country ||
      !userData?.alternateAddress?.line1) &&
    options?.alternateAddress?.required === FieldRequiredType.ERROR &&
    hasAlternateAddress;
  const formDisabled =
    loading ||
    (!updated && updateMode) ||
    physicalAddressInvalid ||
    mailingAddressInvalid ||
    alternateAddressInvalid;

  useEffect(() => {
    setInvalidFieldsList((prev) => {
      const without = prev.filter((key) => key !== 'physicalAddress');
      if (physicalAddressOutOfJurisdiction !== false)
        without.push('physicalAddress');
      if (mailingAddressOutOfJurisdiction !== false)
        without.push('mailingAddress');
      return without;
    });
  }, [physicalAddressOutOfJurisdiction, mailingAddressOutOfJurisdiction]);

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t(updateMode ? 'update' : 'continue'),
        disabled: formDisabled,
        dataTestId: `non-individual-information-${kebabCase(translateBackend(options.title))}-continue-button`,
      }}
    >
      {options?.title && (
        <Typography variant='displayLarge' sx={{ mt: 1 }}>
          {translateBackend(options?.title)}
        </Typography>
      )}
      {options?.subtitle && (
        <Typography variant='bodyLarge' sx={{ mb: 3 }}>
          <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
            {translateBackend(options?.subtitle)}
          </ReactMarkdown>
        </Typography>
      )}

      <Grid container spacing={2}>
        {options?.entityName?.enabled && (
          <Grid item xs={12} md={grid ? 6 : 12}>
            <TextField
              testId='non-individual-information-entity-name'
              onChange={(e: any) =>
                update({ ...userData, entityName: e.target.value })
              }
              disabled={
                loading ||
                options?.entityName?.required === FieldRequiredType.NOT_EDITABLE
              }
              locked={
                options?.entityName?.required === FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              label={translateBackend(options?.entityName?.label)}
              fullWidth
              value={userData.entityName ?? ''}
              onBlur={() => setFocused([...focused, 'entityName'])}
              error={error('entityName')}
            />
          </Grid>
        )}
        {options?.type?.enabled && (
          <Grid item xs={12} md={grid ? 6 : 12}>
            <SelectField
              testId='non-individual-information-entity-type'
              onChange={(e: any) =>
                update({ ...userData, type: e.target.value })
              }
              disabled={
                loading ||
                options?.type?.required === FieldRequiredType.NOT_EDITABLE
              }
              locked={
                options?.type?.required === FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              label={translateBackend(options?.type?.label)}
              fullWidth
              value={userData.type ?? ''}
              onBlur={() => setFocused([...focused, 'type'])}
              error={error('type')}
            >
              <MenuItem
                data-testid='entity-type-investment-fund'
                value='INVESTMENT_FUND'
              >
                {t('entityTypes:INVESTMENT_FUND')}
              </MenuItem>
              <MenuItem
                data-testid='entity-type-partnership'
                value='PARTNERSHIP'
              >
                {t('entityTypes:PARTNERSHIP')}
              </MenuItem>
              <MenuItem
                data-testid='entity-type-private-company'
                value='PRIVATE_COMPANY'
              >
                {t('entityTypes:PRIVATE_COMPANY')}
              </MenuItem>
              <MenuItem
                data-testid='entity-type-publicly-listed-entity'
                value='PUBLICLY_LISTED_ENTITY'
              >
                {t('entityTypes:PUBLICLY_LISTED_ENTITY')}
              </MenuItem>
              <MenuItem
                data-testid='entity-type-regulated-entity'
                value='REGULATED_ENTITY'
              >
                {t('entityTypes:REGULATED_ENTITY')}
              </MenuItem>
              <MenuItem
                data-testid='entity-type-sole-proprietorship'
                value='SOLE_PROPRIETORSHIP'
              >
                {t('entityTypes:SOLE_PROPRIETORSHIP')}
              </MenuItem>
              <MenuItem data-testid='entity-type-trust' value='TRUST'>
                {t('entityTypes:TRUST')}
              </MenuItem>
              <MenuItem data-testid='entity-type-other' value='OTHER'>
                {t('entityTypes:OTHER')}
              </MenuItem>
            </SelectField>
          </Grid>
        )}
        {options?.typeOfTrust?.enabled &&
          userData.type === EntityTypes.TRUST && (
            <Grid item xs={12} md={grid ? 6 : 12}>
              <SelectField
                testId='non-individual-information-typeOfTrust'
                onChange={(e: any) =>
                  update({ ...userData, typeOfTrust: e.target.value })
                }
                disabled={
                  loading ||
                  options?.typeOfTrust?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
                locked={
                  options?.typeOfTrust?.required ===
                  FieldRequiredType.NOT_EDITABLE
                }
                lockMessage={lockMessage()}
                label={translateBackend(options?.typeOfTrust?.label)}
                fullWidth
                value={userData.typeOfTrust}
                onBlur={() => setFocused([...focused, 'typeOfTrust'])}
                error={error('typeOfTrust')}
              >
                <MenuItem data-testid='typeOfTrust-complex' value='COMPLEX'>
                  {t('trustTypes:COMPLEX')}
                </MenuItem>
                <MenuItem data-testid='typeOfTrust-simple' value='SIMPLE'>
                  {t('trustTypes:SIMPLE')}
                </MenuItem>
                <MenuItem data-testid='typeOfTrust-grantor' value='GRANTOR'>
                  {t('trustTypes:GRANTOR')}
                </MenuItem>
              </SelectField>
            </Grid>
          )}
        {options?.physicalAddress?.enabled && (
          <Grid item xs={12}>
            <AddressField
              testId='non-individual-information-address-field'
              onChange={(e: any) => update({ ...userData, physicalAddress: e })}
              disabled={
                loading ||
                options?.physicalAddress?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackend(options?.physicalAddress?.label)}
              lockMessage={lockMessage()}
              fullWidth
              address={userData.physicalAddress}
              onFocus={() => setFocused([...focused, 'physicalAddress'])}
              error={error('physicalAddress')}
            />
          </Grid>
        )}
        {options?.mailingAddress?.enabled && (
          <Grid item xs={12}>
            <RadioGroup
              label={t('components:address.mailAddressCheck')}
              value={hasMailingAddress === true ? 'yes' : 'no'}
              onChange={(e: any) => {
                setHasMailingAddress(e.target.value === 'yes');
                update({
                  ...userData,
                  mailingAddress:
                    e.target.value === 'yes'
                      ? userData.mailingAddress
                      : undefined,
                });
              }}
              disabled={
                loading ||
                options?.mailingAddress?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
            >
              <Radio
                disabled={
                  loading ||
                  options?.mailingAddress?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
                value={'no'}
                label={t('components:booleanOptions.no')}
              />
              <Radio
                disabled={
                  loading ||
                  options?.mailingAddress?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
                value={'yes'}
                label={t('components:booleanOptions.yes')}
              />
            </RadioGroup>
          </Grid>
        )}
        {options?.mailingAddress?.enabled && hasMailingAddress && (
          <Grid item xs={12}>
            <AddressField
              testId='non-individual-information-mailing-address-field'
              onChange={(e: any) => update({ ...userData, mailingAddress: e })}
              disabled={
                loading ||
                options?.mailingAddress?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackend(options?.mailingAddress?.label)}
              lockMessage={lockMessage()}
              fullWidth
              address={userData.mailingAddress}
              onFocus={() => setFocused([...focused, 'mailingAddress'])}
              error={error('mailingAddress')}
            />
          </Grid>
        )}
        {(physicalAddressOutOfJurisdiction !== false ||
          (mailingAddressOutOfJurisdiction !== false && hasMailingAddress)) && (
          <Grid item xs={12}>
            <Alert
              severity={
                options?.physicalAddress?.required === 'ERROR'
                  ? 'error'
                  : 'info'
              }
            >
              {physicalAddressOutOfJurisdiction ||
                mailingAddressOutOfJurisdiction}
            </Alert>
          </Grid>
        )}
        {options?.alternateAddress?.enabled && (
          <Grid item xs={12}>
            <RadioGroup
              label={t('components:address.alternateAddressCheck')}
              value={hasAlternateAddress === true ? 'yes' : 'no'}
              onChange={(e: any) => {
                setHasAlternateAddress(e.target.value === 'yes');
                update({
                  ...userData,
                  alternateAddress:
                    e.target.value === 'yes'
                      ? userData.alternateAddress
                      : undefined,
                });
              }}
              disabled={
                loading ||
                options?.alternateAddress?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
            >
              <Radio
                disabled={
                  loading ||
                  options?.alternateAddress?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
                value={'no'}
                label={t('components:booleanOptions.no')}
              />
              <Radio
                disabled={
                  loading ||
                  options?.alternateAddress?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
                value={'yes'}
                label={t('components:booleanOptions.yes')}
              />
            </RadioGroup>
          </Grid>
        )}
        {options?.alternateAddress?.enabled && hasAlternateAddress && (
          <Grid item xs={12}>
            <AlternativeAddressField
              testId='non-individual-information-address-field'
              onChange={(e: any) =>
                update({ ...userData, alternateAddress: e })
              }
              disabled={
                loading ||
                options?.alternateAddress?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              locked={
                options?.alternateAddress?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackend(options?.alternateAddress?.label)}
              address={userData.alternateAddress}
              error={error('alternateAddress')}
            />
          </Grid>
        )}
        {options?.inProvinceSince?.enabled &&
          userData.physicalAddress?.province === 'QC' && (
            <Grid item xs={12} md={grid ? 6 : 12}>
              <DateField
                dataTestId='non-individual-information-in-province-since'
                onChange={(date: any) =>
                  update({
                    ...userData,
                    inProvinceSince: dayjs(date?.toString()).format(
                      'YYYY-MM-DD',
                    ),
                  })
                }
                disabled={
                  loading ||
                  options?.inProvinceSince?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
                label={translateBackend(options?.inProvinceSince?.label)}
                lockMessage={lockMessage()}
                fullWidth
                value={userData.inProvinceSince}
                onBlur={() => setFocused([...focused, 'inProvinceSince'])}
                error={error('inProvinceSince')}
              />
            </Grid>
          )}
        {options?.primaryEmail?.enabled && (
          <Grid item xs={12} md={grid ? 6 : 12}>
            <TextField
              testId='non-individual-information-primary-email'
              onChange={(e: any) =>
                update({ ...userData, primaryEmail: e.target.value })
              }
              disabled={
                loading ||
                options?.primaryEmail?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
              locked={
                options?.primaryEmail?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              label={translateBackend(options?.primaryEmail?.label)}
              fullWidth
              value={userData.primaryEmail ?? ''}
              onBlur={() => setFocused([...focused, 'primaryEmail'])}
              error={error('primaryEmail')}
              errorText={t('components:fieldErrors.email')}
            />
          </Grid>
        )}
        {options?.phone?.enabled && (
          <Grid item xs={12} md={grid ? 6 : 12}>
            <PhoneNumberField
              testId='non-individual-information-phone'
              onChange={(e: any) => {
                update({ ...userData, phone: e.value });
                setPhoneCountryCode(e.countryCode);
              }}
              disabled={
                loading ||
                options?.phone?.required === FieldRequiredType.NOT_EDITABLE
              }
              locked={
                options?.phone?.required === FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              label={translateBackend(options?.phone?.label)}
              fullWidth
              value={userData.phone ?? ''}
              onBlur={() => setFocused([...focused, 'phone'])}
              error={error('phone') || invalidPhone}
              errorText={
                invalidPhone ? t('components:fieldErrors.phone') : undefined
              }
            />
          </Grid>
        )}
        {options?.companyType?.enabled && (
          <Grid item xs={12} md={grid ? 6 : 12}>
            <TextField
              testId='non-individual-information-company-type'
              onChange={(e: any) =>
                update({ ...userData, companyType: e.target.value })
              }
              disabled={
                loading ||
                options?.companyType?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
              locked={
                options?.companyType?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              label={translateBackend(options?.companyType?.label)}
              fullWidth
              value={userData.companyType ?? ''}
              onBlur={() => setFocused([...focused, 'companyType'])}
              error={error('companyType')}
            />
          </Grid>
        )}
        {options?.registrationNumber?.enabled && (
          <Grid item xs={12} md={grid ? 6 : 12}>
            <TextField
              testId='non-individual-information-registration-number'
              onChange={(e: any) =>
                update({ ...userData, registrationNumber: e.target.value })
              }
              disabled={
                loading ||
                options?.registrationNumber?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
              locked={
                options?.registrationNumber?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              label={translateBackend(options?.registrationNumber?.label)}
              fullWidth
              value={userData.registrationNumber ?? ''}
              onBlur={() => setFocused([...focused, 'registrationNumber'])}
              error={error('registrationNumber')}
            />
          </Grid>
        )}
        {options?.establishedDate?.enabled && (
          <Grid item xs={12} md={grid ? 6 : 12}>
            <DateField
              dataTestId='non-individual-information-established-date'
              onChange={(date: any) =>
                update({
                  ...userData,
                  establishedDate: dayjs(date?.toString()).format('YYYY-MM-DD'),
                })
              }
              disabled={
                loading ||
                options?.establishedDate?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackend(options?.establishedDate?.label)}
              lockMessage={lockMessage()}
              fullWidth
              value={userData.establishedDate}
              onBlur={() => setFocused([...focused, 'establishedDate'])}
              error={error('establishedDate')}
            />
          </Grid>
        )}
        {options?.countryOfRegistration?.enabled && (
          <Grid item xs={12} md={grid ? 6 : 12}>
            <SelectField
              testId='non-individual-information-country-of-registration'
              onChange={(e: any) =>
                update({
                  ...userData,
                  countryOfRegistration: e.target.value,
                })
              }
              disabled={
                loading ||
                options?.countryOfRegistration?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
              locked={
                options?.countryOfRegistration?.required ===
                FieldRequiredType.NOT_EDITABLE
              }
              lockMessage={lockMessage()}
              label={translateBackend(options?.countryOfRegistration?.label)}
              fullWidth
              value={userData.countryOfRegistration ?? ''}
              onBlur={() => setFocused([...focused, 'countryOfRegistration'])}
              error={error('countryOfRegistration')}
            >
              {activeOrganization.applicableLocalization.countries.map(
                (countryCode, index) => (
                  <MenuItem
                    data-testid={`non-individual-information-country-of-registration-${kebabCase(countryCode)}`}
                    key={index}
                    value={countryCode}
                  >
                    <CountryName code={countryCode} />
                  </MenuItem>
                ),
              )}
            </SelectField>
          </Grid>
        )}
        {options?.preferredMethodOfCommunication?.enabled && (
          <Grid item xs={12}>
            <RadioGroup
              testId='non-individual-information-preferred-method-of-communication'
              onChange={(e: any) => {
                update({
                  ...userData,
                  preferredMethodOfCommunication: e.target.value,
                });
                setFocused([...focused, 'preferredMethodOfCommunication']);
              }}
              disabled={
                loading ||
                options?.preferredMethodOfCommunication?.required ===
                  FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackend(
                options?.preferredMethodOfCommunication?.label,
              )}
              fullWidth
              value={userData.preferredMethodOfCommunication}
              onFocus={() =>
                setFocused([...focused, 'preferredMethodOfCommunication'])
              }
              error={error('preferredMethodOfCommunication')}
            >
              <Radio
                testId='non-individual-information-preferred-method-of-communication-email'
                value='EMAIL'
                disabled={
                  loading ||
                  options?.preferredMethodOfCommunication?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
                label={t('edit.preferredMethodOfCommunicationOptions.EMAIL')}
              />
              <Radio
                testId='non-individual-information-preferred-method-of-communication-phone'
                value='PHONE'
                disabled={
                  loading ||
                  options?.preferredMethodOfCommunication?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
                label={t('edit.preferredMethodOfCommunicationOptions.PHONE')}
              />
              <Radio
                testId='non-individual-information-preferred-method-of-communication-text'
                value='TEXT'
                disabled={
                  loading ||
                  options?.preferredMethodOfCommunication?.required ===
                    FieldRequiredType.NOT_EDITABLE
                }
                label={t('edit.preferredMethodOfCommunicationOptions.TEXT')}
              />
            </RadioGroup>
          </Grid>
        )}
        {options?.language?.enabled && (
          <Grid item xs={12} md={grid ? 6 : 12}>
            <RadioGroup
              testId='non-individual-information-preferred-language'
              onChange={(e: any) => {
                update({ ...userData, language: e.target.value });
                setFocused([...focused, 'language']);
              }}
              disabled={
                loading ||
                options?.language?.required === FieldRequiredType.NOT_EDITABLE
              }
              label={translateBackend(options?.language?.label)}
              fullWidth
              value={userData.language}
              onFocus={() => setFocused([...focused, 'language'])}
              error={error('language')}
            >
              {getOrgLanguages().map((language) => (
                <Radio
                  key={language}
                  testId={`non-individual-information-preferred-language-${language.toLowerCase()}`}
                  value={language}
                  disabled={
                    loading ||
                    options?.language?.required ===
                      FieldRequiredType.NOT_EDITABLE
                  }
                  label={getLanguageDisplayName(language)}
                />
              ))}
            </RadioGroup>
          </Grid>
        )}

        <Grid item xs={12}>
          {renderCustomFields({
            customData: userData,
            update,
            focused,
            setFocused,
            loading,
            ignoreCustomFieldTriggerRules,
            userId: userData.id,
          })}
        </Grid>
      </Grid>
    </WorkflowCompletionForm>
  );
};

export default NonIndividualInformationVisual;
